// iter v29 r216 — Pubblica Veloce su iOS · sostituto mobile dell'estensione.
//
// User M1506: «dall'iPhone come fa l'agente a compilare il form del portale?»
// Chrome iOS non supporta extension (limite Apple). Soluzione:
//
// 1. L'agente apre /listings/{public_id}/pubblica-veloce
// 2. Vede tutti i campi del suo annuncio in card grandi, ognuna col tasto Copia
// 3. Tocca "Apri Idealista nuovo annuncio" → si apre Safari sul modulo del portale
// 4. Per ogni campo: torna su Pronto (App Switcher) → tap Copia → torna su Safari → incolla
//
// Tedioso? Sì. Ma è l'unico modo su iOS, ed è 10x più veloce che ricordare a memoria.

import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy, CheckCircle, ArrowLeft, ArrowSquareOut, Browsers,
  Globe, BuildingOffice, Sparkle, House,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";

// Link al form "nuovo annuncio" dei portali (Safari li apre, e se l'agente
// è loggato lo porta dritto al modulo; altrimenti login → poi modulo).
const PORTALS = [
  {
    key: "idealista",
    name: "Idealista",
    color: "#5BBC9E",
    publishUrl: "https://www.idealista.it/areaprivata/pubblica-nuovo-annuncio/",
  },
  {
    key: "immobiliare",
    name: "Immobiliare.it",
    color: "#E61D2B",
    publishUrl: "https://www.immobiliare.it/inserisci-annunci/",
  },
];

// Tutti i possibili campi che i portali chiedono in fase di pubblicazione.
// Ordine ottimizzato per il flusso reale del form Idealista/Immobiliare.
function fieldsFromProperty(p) {
  if (!p) return [];
  const arr = [
    { key: "title", label: "Titolo annuncio", value: p.title, lines: 1 },
    { key: "price", label: "Prezzo (€)", value: p.price ? String(Number(p.price).toFixed(0)) : "", lines: 1, hint: "Solo numeri, niente € niente virgole" },
    { key: "city", label: "Città", value: p.city, lines: 1 },
    { key: "zona", label: "Zona / Quartiere", value: p.neighborhood || p.area, lines: 1 },
    { key: "address", label: "Indirizzo (via + numero)", value: p.address, lines: 1, hint: p.hide_address ? "Indirizzo nascosto al pubblico nei tuoi listing" : "Solo se hai messo l'indirizzo pubblico" },
    { key: "property_type", label: "Tipologia immobile", value: p.property_type, lines: 1 },
    { key: "rooms", label: "Numero locali", value: p.rooms || p.locali ? String(p.rooms || p.locali) : "", lines: 1 },
    { key: "surface", label: "Superficie (mq)", value: p.surface || p.size_mq ? String(p.surface || p.size_mq) : "", lines: 1 },
    { key: "bathrooms", label: "Bagni", value: p.bathrooms ? String(p.bathrooms) : "", lines: 1 },
    { key: "floor", label: "Piano", value: p.floor ? String(p.floor) : "", lines: 1 },
    { key: "energy_class", label: "Classe energetica", value: (p.energy_class || "").toUpperCase(), lines: 1 },
    { key: "year_built", label: "Anno costruzione", value: p.year_built ? String(p.year_built) : "", lines: 1 },
    { key: "description", label: "Descrizione lunga", value: p.ai_description || p.description, lines: 6 },
  ];
  // Tieni solo campi compilati
  return arr.filter((f) => f.value && String(f.value).trim().length > 0);
}

export default function PubblicaVeloce() {
  const { id } = useParams();  // public_id del listing
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        // iter v29 r216 — Endpoint auth /api/listings/by-public-id/{public_id}/full
        // restituisce property+listing flat con campi raw (l'agente è owner).
        const { data: payload } = await api.get(`/listings/by-public-id/${id}/full`);
        if (on) setData(payload);
      } catch {
        if (on) toast.error("Annuncio non trovato");
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [id]);

  const copy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1200);
      toast.success("Copiato");
      // Haptic native iOS
      try {
        if (window.Capacitor?.isNativePlatform?.()) {
          const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
          Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
        }
      } catch { /* no-op */ }
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const fields = fieldsFromProperty(data);

  return (
    <AppShell title="Pubblica veloce" breadcrumb="Pubblica veloce">
      <div className="max-w-3xl mx-auto pb-24" data-testid="pubblica-veloce">
        {/* Back link */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-[13px] text-[#6B6A65] hover:text-[#1C1C1A] mb-3"
          data-testid="pv-back"
        >
          <ArrowLeft size={13} weight="bold" />
          Indietro
        </button>

        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] mb-1">
          <Sparkle size={11} weight="fill" />
          Pubblicazione assistita
        </div>
        <h1 className="font-editorial text-[26px] sm:text-[32px] leading-tight text-[#1C1C1A]">
          {loading ? "Caricamento…" : data?.title || "Annuncio"}
        </h1>
        <p className="text-[13px] text-[#6B6A65] mt-1.5 leading-snug max-w-xl">
          Tocca <strong>Apri</strong> il portale, poi torna qui e <strong>copia</strong> i campi uno alla volta. Su iPhone si fa con l'App Switcher (doppio tap sul tasto Home o swipe in basso). Cinque minuti e l'annuncio è online.
        </p>

        {/* Bottoni portali */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {PORTALS.map((p) => (
            <a
              key={p.key}
              href={p.publishUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#1C1C1A] hover:bg-black text-white rounded-xl p-4 transition-colors flex items-center justify-between gap-3 group"
              data-testid={`pv-open-${p.key}`}
              data-haptic="1"
            >
              <div>
                <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Apri</div>
                <div className="font-editorial text-[20px] leading-tight">{p.name}</div>
                <div className="text-[11px] opacity-70">Modulo nuovo annuncio</div>
              </div>
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                <ArrowSquareOut size={18} weight="bold" />
              </div>
            </a>
          ))}
        </div>

        {/* Lista campi */}
        {!loading && fields.length === 0 && (
          <div className="mt-6 bg-white border border-[#E8D9A8] rounded-xl p-5 text-center">
            <p className="text-[13.5px] text-[#1C1C1A] font-semibold">Questo annuncio non ha ancora dati compilati</p>
            <p className="text-[12.5px] text-[#6B6A65] mt-1">Apri l'annuncio e aggiungi prezzo, foto, descrizione prima di pubblicare.</p>
            <Link
              to={`/dashboard/property/${data?.property_id || id}`}
              className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-[#7C8B6E]"
            >
              Apri annuncio →
            </Link>
          </div>
        )}

        {!loading && fields.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#9A968B] mb-2.5 px-1">
              {fields.length} campi da copiare
            </div>
            <div className="space-y-2">
              {fields.map((f) => {
                const isCopied = copiedKey === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => copy(f.key, f.value)}
                    className={`w-full text-left bg-white border rounded-xl p-3.5 transition-colors flex items-start gap-3 ${
                      isCopied ? "border-[#7C8B6E] bg-[#F1F4ED]" : "border-[#E5E7E0] hover:border-[#7C8B6E] active:bg-[#F8F7F2]"
                    }`}
                    data-testid={`pv-field-${f.key}`}
                    data-haptic="1"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[10.5px] uppercase tracking-wider font-bold text-[#9A968B]">
                        {f.label}
                      </div>
                      <div className={`mt-1 text-[14px] text-[#1C1C1A] leading-snug ${f.lines > 1 ? "" : "truncate"}`}>
                        {f.value}
                      </div>
                      {f.hint && (
                        <div className="mt-1 text-[10.5px] text-[#9A968B]">{f.hint}</div>
                      )}
                    </div>
                    <div className="shrink-0 mt-1">
                      {isCopied ? (
                        <span className="inline-flex items-center gap-1 text-[#5C6B4E] text-[11.5px] font-bold">
                          <CheckCircle size={14} weight="fill" />
                          Copiato
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#7C8B6E] text-[11.5px] font-semibold">
                          <Copy size={13} weight="bold" />
                          Copia
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6 bg-[#F8F7F2] border border-[#EAE7DF] rounded-xl p-4">
          <div className="flex items-start gap-2.5">
            <Globe size={16} weight="duotone" className="text-[#7C8B6E] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[12.5px] text-[#1C1C1A] font-semibold mb-1">Modo più veloce: feed XML</p>
              <p className="text-[11.5px] text-[#6B6A65] leading-snug">
                Se il tuo account portale supporta l'import XML, fai il copia-incolla solo della prima volta dell'URL: poi tutto è automatico. Vai su <Link to="/dashboard/portali" className="underline text-[#7C8B6E]">Portali</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
